import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Layout } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow } from "@/components/site/Eyebrow";
import { breadcrumbScript } from "@/lib/schema";

export const Route = createFileRoute("/book")({
  component: BookPage,
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex, follow" },
      { title: "Book Your Vehicle | Avolus Luxury Car Rental Vancouver" },
      {
        name: "description",
        content:
          "Book your luxury or exotic car rental online with Avolus. Instant availability and confirmation across Metro Vancouver, YVR, and Whistler.",
      },
      { property: "og:title", content: "Book Your Vehicle | Avolus" },
      { property: "og:description", content: "Reserve your Avolus vehicle online in minutes." },
      { property: "og:url", content: "https://avoluscars.com/book" },
    ],
    links: [{ rel: "canonical", href: "https://avoluscars.com/book" }],
    scripts: [
      breadcrumbScript([
        { name: "Home", url: "https://avoluscars.com/" },
        { name: "Book", url: "https://avoluscars.com/book" },
      ]),
    ],
  }),
});

const WIDGET_SRC =
  "https://avolus-cars.hqrentals.app/public/car-rental/integrations/assets/integrator";

function BookPage() {
  const [widgetLoaded, setWidgetLoaded] = useState(false);

  useEffect(() => {
    const triggerWidget = () => {
      document.dispatchEvent(new Event("hqrs:integrator:trigger"));
    };

    document.querySelectorAll(`script[src="${WIDGET_SRC}"]`).forEach((s) => s.remove());

    const script = document.createElement("script");
    script.src = WIDGET_SRC;
    script.async = true;
    script.onload = triggerWidget;
    document.body.appendChild(script);

    // If the script was already cached and loaded synchronously in a prior visit,
    // dispatching the trigger immediately is also safe - the listener is idempotent.
    const triggerTimeout = window.setTimeout(triggerWidget, 100);

    const container = document.querySelector(".hq-rental-software-integration");
    let observer: MutationObserver | null = null;
    if (container) {
      observer = new MutationObserver(() => {
        if (container.querySelector("iframe")) {
          setWidgetLoaded(true);
          observer?.disconnect();
        }
      });
      observer.observe(container, { childList: true, subtree: true });
    }

    // Fallback: hide spinner after 8s regardless so a slow widget isn't blocked behind it.
    const fallback = window.setTimeout(() => setWidgetLoaded(true), 8000);

    return () => {
      window.clearTimeout(triggerTimeout);
      window.clearTimeout(fallback);
      observer?.disconnect();
      document.querySelectorAll(`script[src="${WIDGET_SRC}"]`).forEach((s) => s.remove());
    };
  }, []);

  return (
    <Layout showWhyAvolus={false} bookingPage>
      <div className="bg-white">
        <section className="pt-40 pb-12">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <Reveal>
              <Eyebrow>Reservations</Eyebrow>
              <h1 className="font-display text-5xl md:text-7xl text-foreground">
                Book Your Vehicle
              </h1>
              <p className="mt-6 text-muted-foreground max-w-2xl">
                Select your dates and vehicle below. Instant availability and confirmation.
              </p>
            </Reveal>
          </div>
        </section>

        <section className="pb-24">
          <div className="mx-auto max-w-none px-3 sm:px-5 lg:px-8">
            <div
              className="relative bg-white px-0 py-4 md:py-6"
              style={{ minHeight: "80vh" }}
            >
              {!widgetLoaded && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 pointer-events-none">
                  <div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" />
                  <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                    Loading booking engine
                  </p>
                </div>
              )}
              <div
                className="hq-rental-software-integration"
                data-integrator_link="https://avolus-cars.hqrentals.app/public/car-rental/integrations"
                data-brand="9r9wceju-2hie-y65s-y8cl-ohqnfmbiyqfa"
                data-snippet="reservations"
                data-skip_language=""
                data-rate_type_uuid=""
                data-referral=""
                data-enable_auto_language_update=""
              />
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}
